/**
 * UK date formatting.
 *
 * `toLocaleDateString()` with no locale follows the BROWSER's locale, which on a
 * US-configured machine renders 18 August as "8/18/2026". Cluubz is a UK app, so
 * dates are formatted explicitly rather than left to whatever the device says.
 *
 * The default form spells the month — "18 Aug 2026" — because 08/09 is genuinely
 * ambiguous to a reader who does not know which convention is in play, and the
 * planner names use the same style.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Parse to Y/M/D without letting a timezone shift the day. */
const partsOf = (value: string | Date): [number, number, number] | null => {
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return [value.getFullYear(), value.getMonth() + 1, value.getDate()];
  }
  const str = String(value || '');
  const iso = str.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split('-').map(Number);
    return [y, m, d];
  }
  const parsed = new Date(str);
  if (isNaN(parsed.getTime())) return null;
  return [parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate()];
};

/** "18 Aug 2026" */
export const formatDateUK = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  const parts = partsOf(value);
  if (!parts) return '';
  const [y, m, d] = parts;
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

/** "18 Aug" — for when the year is obvious from context. */
export const formatDayMonthUK = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  const parts = partsOf(value);
  if (!parts) return '';
  const [, m, d] = parts;
  return `${d} ${MONTHS[m - 1]}`;
};

/** "18/08/2026" — for dense lists where the spelled month will not fit. */
export const formatNumericUK = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  const parts = partsOf(value);
  if (!parts) return '';
  const [y, m, d] = parts;
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
};

/**
 * "2026-09-16" for the LOCAL calendar day.
 *
 * `new Date().toISOString().split('T')[0]` looks equivalent but returns the UTC
 * day, so during BST every local time between midnight and 01:00 reports
 * yesterday — the home screen would show the wrong day's activities for an hour
 * every night. Built from the local getters for the same reason `partsOf` does.
 */
export const toLocalDateKey = (value: Date = new Date()): string => {
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
